import React, { useState } from 'react';
import { Dropdown, Flex, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  AtRiskTringleIcon,
  ChevronDownIcon,
  CriticalCircleIcon,
  GreenTickCircleOutlineIcon,
} from '@/assets/icons/icons';
import { GxpSensorOption } from './types';

const successIcon = (
  <GreenTickCircleOutlineIcon color='var(--green-900)' width={12} height={12} />
);

const warningIcon = (
  <AtRiskTringleIcon color='var(--warning)' width={12} height={12} />
);

const criticalIcon = (
  <CriticalCircleIcon color='var(--danger)' width={12} height={12} />
);

export const ICONS_BY_STATUS: Record<string, React.ReactNode> = {
  pass: successIcon,
  published: successIcon,
  published_auto: successIcon,
  published_after_review: successIcon,

  warning: warningIcon,
  warning_pending: warningIcon,
  awaiting_data: warningIcon,
  finalized_missing_data: warningIcon,
  to_review: warningIcon,

  critical: criticalIcon,
  danger: criticalIcon,
  requires_review: criticalIcon,
  rejected: criticalIcon,

  other: null,
};

interface GxpReportTitleSectionProps {
  referenceID?: string;
  originAddress?: string;
  sensors?: GxpSensorOption[];
  activeSensorId?: string;
  onSensorChange?: (sensorId: string) => void;
}

const GxpReportTitleSection: React.FC<GxpReportTitleSectionProps> = ({
  referenceID,
  originAddress,
  sensors,
  activeSensorId,
  onSensorChange,
}) => {
  const [open, setOpen] = useState(false);
  const activeOption =
    sensors.find((option) => option?.sensor_tracking_id === activeSensorId) ??
    sensors[0];

  const menuItems: MenuProps['items'] = sensors?.map((option) => ({
    key: option?.sensor_tracking_id,
    label: (
      <div className='gxp-sensor-menu-item'>
        <span>{option?.label}</span>
        <span className={`gxp-status-tag gxp-status-tag--${option?.status}`}>
          {ICONS_BY_STATUS[option?.status ?? 'other']}
          {option?.status_label}
        </span>
      </div>
    ),
  }));

  return (
    <div className='gxp-title-section'>
      <div className='gxp-title-section__main'>
        <Typography.Text
          className='gxp-title-section__id'
          ellipsis={{ tooltip: referenceID }}
        >
          {referenceID}
        </Typography.Text>
        <div className='gxp-title-section__route'>{originAddress}</div>
      </div>

      {activeOption && (
        <Dropdown
          trigger={['click']}
          open={open}
          onOpenChange={setOpen}
          menu={{
            items: menuItems,
            onClick: ({ key }) => {
              onSensorChange(key);
              setOpen(false);
            },
          }}
        >
          <button type='button' className='gxp-sensor-select'>
            <Typography.Text className='gxp-sensor-select__label' ellipsis>
              {activeOption?.label}
            </Typography.Text>
            <Flex align='center' gap={8}>
              <span
                className={`gxp-status-tag gxp-status-tag--${activeOption?.status}`}
              >
                {ICONS_BY_STATUS[activeOption?.status ?? 'other']}
                {activeOption?.status_label}
              </span>
              <ChevronDownIcon size={16} color='var(--text-secondary)' />
            </Flex>
          </button>
        </Dropdown>
      )}
    </div>
  );
};

export default GxpReportTitleSection;
